import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Card, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import Constants from "Helpers/Constants";

function GalleryEditor({
  title,
  submitLabel,
  initialValues,
  onSubmit,
  submitLoading,
  notification,
  notificationColor,
  notificationMessage,
}) {
  const [form, setForm] = useState({
    title: "",
    caption: "",
    sort_order: 0,
    status: "active",
    image: null,
    existingImage: "",
  });

  useEffect(() => {
    if (initialValues) {
      setForm({
        title: initialValues.title || "",
        caption: initialValues.caption || "",
        sort_order: initialValues.sort_order || 0,
        status: initialValues.status || "active",
        image: null,
        existingImage: initialValues.image || "",
      });
    }
  }, [initialValues]);

  const imagePreview = useMemo(() => {
    if (form.image) {
      return URL.createObjectURL(form.image);
    }

    if (form.existingImage) {
      return `${Constants.imageDomain}${form.existingImage}`;
    }

    return "";
  }, [form.image, form.existingImage]);

  useEffect(
    () => () => {
      if (form.image && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    },
    [form.image, imagePreview]
  );

  const updateField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(form);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              {title}
            </MDTypography>
          </MDBox>
          <MDBox py={3} px={2}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InputLabel>Gallery Title</InputLabel>
                  <TextField
                    fullWidth
                    required
                    value={form.title}
                    onChange={(e) => updateField("title", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InputLabel>Sort Order</InputLabel>
                  <TextField
                    fullWidth
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => updateField("sort_order", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    fullWidth
                    value={form.status}
                    onChange={(e) => updateField("status", e.target.value)}
                    style={{ height: "45px", color: "#111" }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel>Caption</InputLabel>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={form.caption}
                    onChange={(e) => updateField("caption", e.target.value)}
                    helperText="Short supporting text shown below the image on the website."
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputLabel>Gallery Image</InputLabel>
                  <TextField
                    fullWidth
                    type="file"
                    focused
                    required={!form.existingImage}
                    helperText="Use a high-quality image for the website gallery."
                    onChange={(e) => updateField("image", e.target.files?.[0] || null)}
                  />
                </Grid>
                {imagePreview ? (
                  <Grid item xs={12}>
                    <MDBox
                      component="img"
                      src={imagePreview}
                      alt="Gallery preview"
                      sx={{
                        width: "100%",
                        maxWidth: 520,
                        borderRadius: "18px",
                        border: "1px solid rgba(24,53,38,0.12)",
                        objectFit: "cover",
                      }}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12}>
                  <MDButton type="submit" variant="contained" color="primary" disabled={submitLoading}>
                    {submitLoading ? "Saving..." : submitLabel}
                  </MDButton>
                </Grid>
              </Grid>
            </form>
          </MDBox>
          <MDSnackbar
            color={notificationColor}
            icon="notifications"
            title="AgriQuiz"
            content={notificationMessage}
            dateTime="Just now"
            open={notification}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

GalleryEditor.propTypes = {
  title: PropTypes.string.isRequired,
  submitLabel: PropTypes.string.isRequired,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  submitLoading: PropTypes.bool,
  notification: PropTypes.bool,
  notificationColor: PropTypes.string,
  notificationMessage: PropTypes.string,
};

GalleryEditor.defaultProps = {
  initialValues: null,
  submitLoading: false,
  notification: false,
  notificationColor: "success",
  notificationMessage: "",
};

export default GalleryEditor;
